import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import RestockProductCard from "../../../../components/Card/RestockProductCard";
import { CustomContainer } from "../../../../components/Containers";
import TextInput from "../../../../components/TextInputs";
import { DEFAULT_VAT_PERCENTAGE } from "../../../../constants/Variables";
import useDebounce from "../../../../hooks/useDebounce";
import {
  restockProduct,
  searchProductBySku,
} from "../../../../services/supplier/productApiFn";
import {
  calculateProductFinalPrice,
  handleOpenSnackBar,
} from "../../../../utils";
import { restockProductValidation } from "../../../../utils/Validation";
import { RestockPricing } from "./Pricing";

const initialInputValues = {
  quantity: 0,
  price: 0,
  discount: 0,
  profitMargin: 0,
};

export const RestockProduct = ({ handleChangeTab }) => {
  // Translation
  const { t } = useTranslation([]);

  // Sku
  const [sku, setSku] = useState("");

  // Loading state
  const [isLoading, setIsLoading] = useState({
    fetch: false,
    save: false,
  });

  // Product detail
  const [productDetail, setProductDetail] = useState(null);

  // Input error
  const [inputError, setInputError] = useState({});

  // Input values
  const [inputValues, setInputValues] = useState(initialInputValues);

  // Check if price is applied
  const [isPriceApplied, setIsPriceApplied] = useState(false);

  // Check if quantity is applied
  const [isQuantityApplied, setIsQuantityApplied] = useState(false);

  // debounce the sku
  const debouncedSku = useDebounce(sku, 1000);

  // get current language
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  // get settings
  const settings = useSelector((state) => state.settingsReducer.settings);

  // fetch product by sku
  const fetchProductBySku = async (sku) => {
    if (!sku) {
      setInputError((prev) => ({
        ...prev,
        sku: t("validation.skuIsRequired"),
      }));
      handleOpenSnackBar("error", t("validation.skuIsRequired"));
      return;
    }
    try {
      setIsLoading((prev) => ({ ...prev, fetch: true }));
      const response = await searchProductBySku(sku);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        setProductDetail(response?.data?.data);

        const isVariant = data?.isVariant;
        const price = isVariant ? data?.variants?.[0]?.price : data?.price;
        const discount = isVariant
          ? data?.variants?.[0]?.discount
          : data?.discount;
        const profitMargin = isVariant
          ? data?.variants?.[0]?.profitMargin
          : data?.profitMargin;

        setInputValues((prev) => ({
          ...prev,
          price: price,
          discount: discount,
          profitMargin: profitMargin,
        }));
      } else {
        setProductDetail(null);
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  useEffect(() => {
    if (debouncedSku) {
      fetchProductBySku(debouncedSku);
    }
  }, [debouncedSku]);

  const renderLoading = () => {
    return (
      <Stack mt={4} justifyContent="center" alignItems="center">
        <CircularProgress size={30} />
      </Stack>
    );
  };

  const renderProductNotFound = () => {
    return (
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={1.5}
        mt={2}
        px={2}
        py={1.5}
        borderRadius={2}
        border="1px solid"
        borderColor="info.light"
        backgroundColor="info.lighter"
      >
        <InfoOutlinedIcon sx={{ color: "info.main", mt: 0.25 }} />
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("supplierProductTab.searchProductInstructionTitle")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {t("supplierProductTab.searchProductInstruction")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("supplierProductTab.searchProductInstructionSubtitle")}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const handleSave = async () => {
    const validate = restockProductValidation(
      inputValues,
      setInputError,
      isPriceApplied,
      isQuantityApplied,
    );

    if (validate) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    // Calculate the final Product amount
    const finalAmount = calculateProductFinalPrice(
      inputValues.price,
      inputValues.discount,
      inputValues.profitMargin,
    );

    const payload = {
      ...(isQuantityApplied && {
        quantity: inputValues.quantity,
      }),
      sku: sku,
      productPriceApply: isPriceApplied,
      productQuantityApply: isQuantityApplied,
      ...(productDetail?.isVariant && {
        variantId: productDetail?.variants?.[0]?._id,
      }),
      ...(isPriceApplied && {
        pricing: {
          discount: inputValues.discount,
          profitMargin: inputValues.profitMargin,
          price: inputValues.price,
          finalPrice: finalAmount,
        },
      }),
    };

    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await restockProduct(productDetail?._id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        setInputValues(initialInputValues);
        setIsPriceApplied(false);
        setSku("");
        setInputError({});
        setProductDetail(null);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const renderQunatityInput = () => {
    return (
      <Box mt={2}>
        <RestockPricing
          inputValues={inputValues}
          setInputValues={setInputValues}
          inputError={inputError}
          setInputError={setInputError}
          vat={settings?.tax || DEFAULT_VAT_PERCENTAGE}
          isPriceApplied={isPriceApplied}
          setIsPriceApplied={setIsPriceApplied}
          isQuantityApplied={isQuantityApplied}
          setIsQuantityApplied={setIsQuantityApplied}
          onSave={handleSave}
        />
      </Box>
    );
  };

  return (
    <CustomContainer
      primaryHeading={t("supplierProductTab.restockProduct")}
      secondaryHeading={t("supplierProductTab.restockProductDescription")}
    >
      <Stack direction="row" gap={2}>
        <TextInput
          value={sku}
          onChange={(e) => {
            setSku(e.target.value);
            setInputError((prev) => ({ ...prev, sku: "" }));
          }}
          placeholder={t("common.searchBySku")}
          inputError={Boolean(inputError.sku)}
          helperText={t(inputError.sku)}
          InputStartIcon={<SearchIcon />}
          InputEndIcon={
            sku?.trim() ? (
              <CancelIcon
                onClick={() => {
                  setSku("");
                  setInputError((prev) => ({ ...prev, sku: "" }));
                  setProductDetail(null);
                }}
              />
            ) : null
          }
        />
      </Stack>

      {!isLoading?.fetch && !productDetail && renderProductNotFound()}
      {isLoading?.fetch && renderLoading()}
      {productDetail && !isLoading?.fetch && (
        <RestockProductCard
          productDetail={productDetail}
          currentLanguage={currentLanguage}
        />
      )}
      {productDetail && !isLoading?.fetch && renderQunatityInput()}
    </CustomContainer>
  );
};
